/**
 * Error taxonomy shared by API and clients. Clients map codes to localized text
 * (`error.<CODE>` in @dijitalapp/i18n); they never show raw server messages to users.
 */
export const ERROR_CODES = [
  'AUTH_INVALID_CREDENTIALS',
  'AUTH_SESSION_EXPIRED',
  'AUTH_REQUIRED',
  'AUTH_ORIGIN_REJECTED',
  'AGE_BELOW_MINIMUM',
  'EMAIL_TAKEN',
  'USERNAME_TAKEN',
  'USERNAME_RESERVED',
  'USERNAME_NOT_ALLOWED',
  'SITE_CHANGED',
  /** Read-aloud (ElevenLabs) is off, has no key or voice, or the service refused (the panel shows why). */
  'VOICE_UNAVAILABLE',
  /** This month's read-aloud characters would go over the panel's limit. */
  'VOICE_LIMIT',
  /** The sermon is already being read aloud. */
  'VOICE_BUSY',
  /** The partner site did not answer as expected. */
  'SOURCE_UNAVAILABLE',
  'QR_LOGIN_EXPIRED',
  'USERNAME_CHANGE_TOO_SOON',
  'VALIDATION_FAILED',
  'RATE_LIMITED',
  'FORBIDDEN',
  'NOT_FOUND',
  'POLICY_LOCKED',
  'SPACE_VERIFICATION_REQUIRED',
  'TERMS_NOT_ACCEPTED',
  /** A supervised child asked to add someone: it waits for a parent's approval (Family Centre). */
  'FAMILY_APPROVAL_PENDING',
  'ENTITLEMENT_LIMIT',
  /** No payment provider is switched on, or the provider did not answer. */
  'PAYMENT_UNAVAILABLE',
  /** The person already owns what they try to buy. */
  'PAYMENT_ALREADY_OWNED',
  /** Children cannot buy anything themselves. */
  'PAYMENT_NOT_ALLOWED',
  /** Sign-in refused because staff suspended the account; the person may appeal. */
  'ACCOUNT_SUSPENDED',
  'EMAIL_TOKEN_INVALID',
  /** The 6-digit code is wrong, expired or used up (a new e-mail brings a new one). */
  'EMAIL_CODE_INVALID',
  'PASSKEY_FAILED',
  'EMAIL_NOT_VERIFIED',
  /** Recipient blocked the sender, or their privacy settings do not accept the sender. Deliberately not distinguished. */
  'MESSAGING_NOT_ALLOWED',
  /** A message request is waiting: no more messages (MESSAGE_REQUEST_MAX) until the other person accepts. */
  'MESSAGE_REQUEST_PENDING',
  'MESSAGE_TOO_LARGE',
  /** Slow mode: the member must wait before the next message (details.retryAfterSeconds). */
  'SLOW_MODE',
  /** A locked chat: this device must be unlocked (secret code or passkey) first. */
  'CHAT_LOCKED',
  'CHAT_LOCK_WRONG_CODE',
  /** Locking a chat needs a secret code to be set first. */
  'CHAT_LOCK_NO_CODE',
  'AUTH_TICKET_INVALID',
  'PROTOCOL_INVALID_FRAME',
  'REGISTRATION_CLOSED',
  'INVITE_INVALID',
  'ADMIN_PASSKEY_REQUIRED',
  'MESSAGE_EDIT_EXPIRED',
  'ATTACHMENT_REJECTED',
  /** Resumable upload: the chunk does not start where the server is (details.received says where). */
  'UPLOAD_OFFSET_MISMATCH',
  'STORAGE_QUOTA_EXCEEDED',
  'GROUP_LINK_INVALID',
  'GROUP_FULL',
  'SPACE_LINK_INVALID',
  'EDU_INVITE_INVALID',
  'ATTACHMENT_MALWARE',
  'ATTACHMENT_SCAN_UNAVAILABLE',
  'PIN_LIMIT',
  'CHAT_PIN_LIMIT',
  'FOLDER_LIMIT',
  'E2EE_REQUIRED',
  'E2EE_UNAVAILABLE',
  'E2EE_UNSUPPORTED',
  'BOOKING_SLOT_TAKEN',
  'AI_UNAVAILABLE',
  /** The month's AI spending limit (set by staff) is reached. */
  'AI_BUDGET_EXCEEDED',
  'AI_CONSENT_REQUIRED',
  'AI_QUOTA_EXCEEDED',
  'AI_REFUSED',
  /** The feature behind this route is switched off (its feature flag; e.g. the Market developer program). */
  'FEATURE_NOT_ENABLED',
  /** Someone else changed the record since I read it (its revision moved on): read it again and retry. */
  'EDIT_CONFLICT',
  'UNKNOWN',
] as const;

export type ErrorCode = (typeof ERROR_CODES)[number];

export interface ApiErrorBody {
  error: {
    code: ErrorCode;
    /** Developer-facing detail. Never contains secrets or other users' data. */
    message: string;
    requestId?: string;
    fields?: Record<string, string>;
  };
}

export const ERROR_HTTP_STATUS: Record<ErrorCode, number> = {
  AUTH_INVALID_CREDENTIALS: 401,
  AUTH_SESSION_EXPIRED: 401,
  AUTH_REQUIRED: 401,
  AUTH_ORIGIN_REJECTED: 403,
  AGE_BELOW_MINIMUM: 403,
  EMAIL_TAKEN: 409,
  USERNAME_TAKEN: 409,
  USERNAME_RESERVED: 409,
  USERNAME_NOT_ALLOWED: 422,
  SITE_CHANGED: 409,
  VOICE_UNAVAILABLE: 503,
  VOICE_LIMIT: 429,
  VOICE_BUSY: 409,
  SOURCE_UNAVAILABLE: 502,
  QR_LOGIN_EXPIRED: 410,
  USERNAME_CHANGE_TOO_SOON: 429,
  VALIDATION_FAILED: 400,
  RATE_LIMITED: 429,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  POLICY_LOCKED: 403,
  SPACE_VERIFICATION_REQUIRED: 403,
  TERMS_NOT_ACCEPTED: 400,
  FAMILY_APPROVAL_PENDING: 409,
  ENTITLEMENT_LIMIT: 403,
  PAYMENT_UNAVAILABLE: 503,
  PAYMENT_ALREADY_OWNED: 409,
  PAYMENT_NOT_ALLOWED: 403,
  ACCOUNT_SUSPENDED: 403,
  EMAIL_CODE_INVALID: 400,
  EMAIL_TOKEN_INVALID: 400,
  PASSKEY_FAILED: 400,
  EMAIL_NOT_VERIFIED: 403,
  MESSAGING_NOT_ALLOWED: 403,
  MESSAGE_REQUEST_PENDING: 409,
  MESSAGE_TOO_LARGE: 413,
  SLOW_MODE: 429,
  CHAT_LOCKED: 423,
  CHAT_LOCK_WRONG_CODE: 400,
  CHAT_LOCK_NO_CODE: 409,
  AUTH_TICKET_INVALID: 401,
  PROTOCOL_INVALID_FRAME: 400,
  REGISTRATION_CLOSED: 403,
  INVITE_INVALID: 403,
  ADMIN_PASSKEY_REQUIRED: 403,
  MESSAGE_EDIT_EXPIRED: 403,
  ATTACHMENT_REJECTED: 415,
  UPLOAD_OFFSET_MISMATCH: 409,
  STORAGE_QUOTA_EXCEEDED: 403,
  GROUP_LINK_INVALID: 404,
  GROUP_FULL: 409,
  SPACE_LINK_INVALID: 404,
  EDU_INVITE_INVALID: 404,
  ATTACHMENT_MALWARE: 422,
  ATTACHMENT_SCAN_UNAVAILABLE: 503,
  PIN_LIMIT: 409,
  CHAT_PIN_LIMIT: 409,
  FOLDER_LIMIT: 409,
  E2EE_REQUIRED: 409,
  E2EE_UNAVAILABLE: 409,
  E2EE_UNSUPPORTED: 409,
  BOOKING_SLOT_TAKEN: 409,
  AI_UNAVAILABLE: 503,
  AI_BUDGET_EXCEEDED: 503,
  AI_CONSENT_REQUIRED: 403,
  AI_QUOTA_EXCEEDED: 429,
  AI_REFUSED: 422,
  FEATURE_NOT_ENABLED: 403,
  EDIT_CONFLICT: 409,
  UNKNOWN: 500,
};
